import { useQuery } from "@tanstack/react-query";
import { api } from "../../../../singleton/axios";

export interface StockNews {
  symbol: string;
  publishedDate: string;
  publisher: string;
  title: string;
  image: string;
  site: string;
  text: string;
  url: string;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  count: number;
  hasNextPage: boolean;
}

export interface PaginatedStockNewsResponse {
  data: StockNews[];
  pagination: PaginationInfo;
}

// Helper to check if error is a 404
export const isNewsNotFoundError = (error: Error | null): boolean => {
  if (!error) return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (error as any)?.response?.status === 404;
};

export function useStockNews(ticker: string, page = 0, limit = 10) {
  return useQuery<PaginatedStockNewsResponse, Error>({
    queryKey: ["stockNews", ticker, page, limit],
    queryFn: async () => {
      const { data } = await api.get<PaginatedStockNewsResponse>("/stock-news", {
        params: { ticker, page, limit },
      });
      return data;
    },
    enabled: !!ticker,
    refetchOnWindowFocus: false,
    staleTime: 2.5 * 60 * 1000, // 2.5 minutes for fresh news data
    gcTime: 8 * 60 * 60 * 1000, // 8 hours cache time
    retry: (failureCount, error) => {
      // Don't retry on 404s - legitimately means no news data exists for this ticker
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any)?.response?.status === 404) {
        return false;
      }
      // Retry other errors (500s, network issues) max 2 times
      return failureCount < 2;
    },
  });
}
